import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Custom React hook for managing tooltip visibility and content for a specific field.
 *
 * This hook tracks hover and focus states, and allows registration of a tooltip content provider
 * for a given field key. Tooltip content is dynamically updated based on the current visibility state.
 *
 * @param targetFieldKey - The unique key identifying the field for which the tooltip is managed.
 * @returns An object containing:
 * - `isHovered`: Whether the field is currently hovered.
 * - `isFocused`: Whether the field is currently focused.
 * - `tooltipContent`: The current tooltip content to display, or `null` if none.
 * - `registerTooltipProvider`: Function to register a tooltip content provider for the field.
 * - `rootHoverHandlers`: Mouse event handlers for hover state management.
 * - `focusHandlers`: Focus event handlers for focus state management.
 */
export const useFieldTooltip = (
	targetFieldKey: string,
): {
	isHovered: boolean;
	isFocused: boolean;
	tooltipContent: ReactNode;
	registerTooltipProvider: (
		fieldId: string,
		provider: (() => React.ReactNode | null | undefined) | null,
	) => void;
	rootHoverHandlers: {
		readonly onMouseEnter: () => void;
		readonly onMouseLeave: () => void;
	};
	focusHandlers: {
		readonly onFocus: () => void;
		readonly onBlur: () => void;
	};
} => {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [customTooltipContent, setCustomTooltipContent] = useState<React.ReactNode | null>(null);
	const providerRef = useRef<(() => React.ReactNode | null | undefined) | null>(null);

	const registerTooltipProvider = useCallback(
		(fieldId: string, provider: (() => React.ReactNode | null | undefined) | null): void => {
			if (fieldId !== targetFieldKey) {
				return;
			}
			if (provider == null) {
				if (providerRef.current) {
					providerRef.current = null;
					setCustomTooltipContent(null);
				}
				return;
			}
			providerRef.current = provider;
			if (isHovered || isFocused) {
				try {
					setCustomTooltipContent(provider() ?? null);
				} catch {
					setCustomTooltipContent(null);
				}
			}
		},
		[targetFieldKey, isHovered, isFocused],
	);

	// Recompute on visibility state changes.
	useEffect(() => {
		if (isHovered || isFocused) {
			if (providerRef.current) {
				try {
					setCustomTooltipContent(providerRef.current() ?? null);
				} catch {
					setCustomTooltipContent(null);
				}
			} else {
				setCustomTooltipContent(null);
			}
		} else {
			setCustomTooltipContent(null);
		}
	}, [isHovered, isFocused]);

	const rootHoverHandlers = {
		onMouseEnter: (): void => setIsHovered(true),
		onMouseLeave: (): void => setIsHovered(false),
	} as const;

	const focusHandlers = {
		onFocus: (): void => setIsFocused(true),
		onBlur: (): void => setIsFocused(false),
	} as const;

	return {
		isHovered,
		isFocused,
		tooltipContent: customTooltipContent,
		registerTooltipProvider,
		rootHoverHandlers,
		focusHandlers,
	};
};
